import React, { useEffect, useState, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  Popover,
  Box,
  Typography,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  Warning as WarningIcon,
  Error as CriticalIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { dashboardService, SystemHealth, HealthAlert } from '../services/dashboardService';

// Widget de salud del sistema — parte 2 de la Prioridad 4 del roadmap.
// Consulta /api/dashboard/health-deep cada REFRESH_MS, muestra un dot verde/amarillo/rojo
// en el AppBar y al hacer click abre un popover con detalle por chequeo + alertas activas.
// Solo se monta si el usuario es ADMIN (ver DashboardLayout.tsx).

const REFRESH_MS = 60_000;

type Status = 'ok' | 'warning' | 'critical' | 'unknown' | 'loading';

const statusColors: Record<Status, string> = {
  ok: '#2e7d32',
  warning: '#ed6c02',
  critical: '#d32f2f',
  unknown: '#9e9e9e',
  loading: '#9e9e9e',
};

const statusLabels: Record<Status, string> = {
  ok: 'Todo OK',
  warning: 'Advertencias',
  critical: 'Crítico',
  unknown: 'Sin datos',
  loading: 'Consultando…',
};

function StatusIcon({ status }: { status: Status }) {
  if (status === 'loading') return <CircularProgress size={18} sx={{ color: 'inherit' }} />;
  const color = statusColors[status];
  const props = { sx: { color, fontSize: 22 } };
  if (status === 'ok') return <OkIcon {...props} />;
  if (status === 'warning') return <WarningIcon {...props} />;
  if (status === 'critical') return <CriticalIcon {...props} />;
  return <UnknownIcon {...props} />;
}

function formatUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function checkSummary(area: string, check: any): string {
  if (!check) return '—';
  switch (area) {
    case 'db':
      return `${check.latency_ms}ms`;
    case 'queue':
      if (check.initialized === false) return 'no iniciada';
      return `w:${check.waiting} a:${check.active} f:${check.failed}`;
    case 'circuit_breaker':
      return check.state ?? '—';
    case 'memory':
      return `${check.heap_used_mb} MB heap`;
    case 'websocket':
      return `${check.connected_users} conn`;
    default:
      return '—';
  }
}

const checkLabels: Record<string, string> = {
  db: 'Base de datos',
  queue: 'Cola BullMQ',
  circuit_breaker: 'WhatsApp API',
  memory: 'Memoria',
  websocket: 'WebSocket',
};

const SystemHealthIndicator: React.FC = () => {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const data = await dashboardService.getSystemHealth();
      setHealth(data);
      setStatus(data.status);
      setError(null);
    } catch (err: any) {
      setError(err?.message ?? 'Error consultando estado del sistema');
      setStatus('unknown');
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const id = window.setInterval(fetchHealth, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [fetchHealth]);

  const alertCount = health?.alerts?.length ?? 0;
  const tooltip = statusLabels[status] + (alertCount > 0 ? ` (${alertCount})` : '');

  return (
    <>
      <Tooltip title={`Salud del sistema — ${tooltip}`}>
        <IconButton
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ mr: 1 }}
          aria-label="Salud del sistema"
        >
          <StatusIcon status={status} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '90vw', p: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <StatusIcon status={status} />
          <Typography variant="subtitle1" fontWeight={600}>
            Salud del sistema
          </Typography>
        </Box>

        {error && (
          <Box sx={{ p: 1, mb: 1, borderRadius: 1, bgcolor: 'error.dark', color: 'error.contrastText' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        {health && (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Actualizado {new Date(health.timestamp).toLocaleTimeString('es-AR')} · uptime {formatUptime(health.uptime_seconds)}
            </Typography>

            <Divider sx={{ my: 1 }} />

            {alertCount > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Alertas activas
                </Typography>
                {(health.alerts as HealthAlert[]).map((alert, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start',
                      p: 1,
                      mt: 0.5,
                      borderRadius: 1,
                      bgcolor: alert.severity === 'critical' ? 'error.dark' : 'warning.dark',
                      color: 'common.white',
                    }}
                  >
                    <Chip
                      label={alert.severity}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'common.white',
                        height: 20,
                        fontSize: '0.65rem',
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        [{alert.area}]
                      </Typography>
                      <Typography variant="body2">{alert.message}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              Chequeos
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {Object.entries(health.checks).map(([area, check]) => (
                <Box
                  key={area}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    px: 1,
                    borderRadius: 0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: check.ok ? statusColors.ok : statusColors.warning,
                      }}
                    />
                    <Typography variant="body2">{checkLabels[area] ?? area}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {checkSummary(area, check)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
              Se actualiza cada 60 segundos. También hay un cron cada 5 min escribiendo a /opt/crm-whatsapp/backups/health.log
            </Typography>
          </>
        )}

        {!health && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Popover>
    </>
  );
};

export default SystemHealthIndicator;
